/**
 * Zustand store for the persistent deploy drawer.
 *
 * Three states (§4.9):
 *   - idle       → drawer hidden
 *   - active     → small pill in the top bar, click to expand
 *   - expanded   → full stdout stream + step list + controls
 *
 * `activeJobId` outlives navigation; the drawer reads its state here
 * so you can keep browsing while the deploy runs in the background.
 */
import { create } from 'zustand';
import type { DeployJob, DeployStatus, DeployStep } from '@/api/deploy';

export type DrawerVisibility = 'idle' | 'active' | 'expanded';

export interface DeployStoreState {
  activeJobId: string | null;
  visibility: DrawerVisibility;
  status: DeployStatus | null;
  kind: string;
  agent?: string;
  label?: string;
  steps: DeployStep[];
  stdout: string;
  currentStep?: string;
  progress: number;
  startedAt?: string;
  finishedAt?: string;
  error?: string;
  exitCode?: number;

  start: (
    jobId: string,
    opts: { kind: string; agent?: string; label?: string }
  ) => void;
  setStatus: (status: DeployStatus) => void;
  applyJob: (job: DeployJob) => void;
  appendStdout: (chunk: string) => void;
  expand: () => void;
  collapse: () => void;
  dismiss: () => void;
}

export const useDeployStore = create<DeployStoreState>((set, get) => ({
  activeJobId: null,
  visibility: 'idle',
  status: null,
  kind: '',
  agent: undefined,
  label: undefined,
  steps: [],
  stdout: '',
  currentStep: undefined,
  progress: 0,
  startedAt: undefined,
  finishedAt: undefined,
  error: undefined,
  exitCode: undefined,

  start: (jobId, opts) =>
    set({
      activeJobId: jobId,
      visibility: 'active',
      status: 'pending',
      kind: opts.kind,
      agent: opts.agent,
      label: opts.label ?? `${opts.kind}${opts.agent ? ' ' + opts.agent : ''}`,
      steps: [],
      stdout: '',
      currentStep: undefined,
      progress: 0,
      startedAt: new Date().toISOString(),
      finishedAt: undefined,
      error: undefined,
      exitCode: undefined,
    }),

  setStatus: (status) => set({ status }),

  applyJob: (job) => {
    const current = get();
    if (current.activeJobId !== job.job_id) return;

    const currentStep = job.steps.find((s) => s.status === 'running');
    const progress =
      job.steps.length === 0
        ? 0
        : job.steps.filter((s) => s.status === 'succeeded').length / job.steps.length;

    // Auto-expand if the deploy failed so the operator sees the error.
    const nextVisibility: DrawerVisibility =
      job.status === 'failed' && current.visibility === 'active'
        ? 'expanded'
        : current.visibility;

    set({
      status: job.status,
      steps: job.steps,
      stdout: job.stdout || current.stdout,
      currentStep: currentStep?.name,
      progress,
      startedAt: job.started_at,
      finishedAt: job.finished_at,
      error: job.error,
      exitCode: job.exit_code,
      visibility: nextVisibility,
    });
  },

  appendStdout: (chunk) =>
    set((s) => ({ stdout: s.stdout + chunk })),

  expand: () => set((s) => (s.activeJobId ? { visibility: 'expanded' } : s)),
  collapse: () => set((s) => (s.activeJobId ? { visibility: 'active' } : s)),

  dismiss: () =>
    set({
      activeJobId: null,
      visibility: 'idle',
      status: null,
      kind: '',
      agent: undefined,
      label: undefined,
      steps: [],
      stdout: '',
      currentStep: undefined,
      progress: 0,
      startedAt: undefined,
      finishedAt: undefined,
      error: undefined,
      exitCode: undefined,
    }),
}));
